import i18n from '../i18n.ts';

class LanguageService {
  private isInitialized = false;

  /**
   * Initialize language from user data or localStorage
   * This should be called as early as possible in the app lifecycle
   */
  initializeLanguage(userData?: any): void {
    // Wait for i18n to be ready
    if (!i18n.isInitialized) {
      console.log('i18n not ready yet, retrying...');
      setTimeout(() => this.initializeLanguage(userData), 100);
      return;
    }

    let languageToSet = 'en'; // default

    // Priority 1: User data from login/profile
    if (userData?.language_preference) {
      languageToSet = userData.language_preference;
      console.log('Setting language from user data:', languageToSet);
    }
    // Priority 2: Stored language preference
    else if (localStorage.getItem('i18nextLng')) {
      languageToSet = localStorage.getItem('i18nextLng') || 'en';
      console.log('Setting language from localStorage:', languageToSet);
    }
    // Priority 3: Browser language detection
    else {
      const browserLang = navigator.language.split('-')[0];
      if (['en', 'es'].includes(browserLang)) {
        languageToSet = browserLang;
        console.log('Setting language from browser:', languageToSet);
      }
    }

    // Set the language
    if (i18n.language !== languageToSet) {
      console.log('Changing language from', i18n.language, 'to', languageToSet);
      i18n.changeLanguage(languageToSet);
      localStorage.setItem('i18nextLng', languageToSet);
    } else {
      console.log('Language already set to:', languageToSet);
    }

    this.isInitialized = true;
    console.log('Language initialized:', languageToSet, 'from userData:', userData?.language_preference);
  }

  /**
   * Update language preference and persist it
   */
  updateLanguage(newLanguage: string): void {
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('i18nextLng', newLanguage);
    console.log('Language updated to:', newLanguage);
  }

  /**
   * Force re-initialization with new user data
   */
  reinitializeLanguage(userData?: any): void {
    console.log('Force re-initializing language with user data:', userData?.language_preference);
    this.isInitialized = false;
    this.initializeLanguage(userData);
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return i18n.language;
  }

  /**
   * Reset initialization flag (useful for testing)
   */
  reset(): void {
    this.isInitialized = false;
  }

  /**
   * Debug function to check current state
   */
  debug(): void {
    console.log('LanguageService Debug:', {
      isInitialized: this.isInitialized,
      i18nReady: i18n.isInitialized,
      currentLanguage: i18n.language,
      localStorage: localStorage.getItem('i18nextLng'),
      availableLanguages: i18n.languages
    });
  }
}

const languageService = new LanguageService();
export default languageService;
